/**
 * Truy cập màn Tóm tắt văn bản của module `van-ban` (`/api/v1/m/van-ban/summarize*` qua Gateway,
 * ADR-0084). Chọn API thật hay dữ liệu mẫu bằng `dungApiThat()` lúc gọi.
 *
 * Bản giả diễn cả tác vụ nền: văn bản vừa gửi chạy ở trạng thái chờ rồi tự xong sau vài giây, để màn hình
 * chạy đúng vòng hỏi lại 4 giây như với backend thật.
 */
import { dungApiThat, goiApi } from '../api/http';
import type { ApiError } from '../errors';
import { MOCK_CAU_TOM_TAT as CAU, MOCK_TOM_TAT } from '../../mock/van-ban-tom-tat';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  ChiTietTomTat,
  KetQuaChayTomTat,
  KetQuaPhanCongTuTomTat,
  ThemTomTat,
  TomTat,
  TrangTomTat,
} from '../../modules/xu-ly-van-ban/types-tom-tat';

const GOC = '/api/v1/m/van-ban';
/** Màn đọc một lần tối đa 100 văn bản rồi lọc ở client, như bản cũ. */
const MOT_TRANG = { page: 1, page_size: 100 };
/** Tác vụ giả xong sau chừng này. */
const GIA_XONG_MS = 5_000;

const khongThay = (): ApiError => ({ code: 'NOT_FOUND', status: 404, message: CAU.khongThay });
const tuChoi = <T>(loi: ApiError): Promise<T> => mockRequest<null>(null).then(() => Promise.reject(loi));
const bayGio = () => new Date().toISOString();
const idMoi = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `mock-${Date.now()}`;

// ─── Bản giả ─────────────────────────────────────────────────────────────────

let ds: ChiTietTomTat[] = structuredClone(MOCK_TOM_TAT);
/** id → lúc bắt đầu chạy. */
const dangChay = new Map<string, number>();

function capNhatTacVu() {
  const nay = Date.now();
  for (const [id, luc] of dangChay) {
    if (nay - luc < GIA_XONG_MS) continue;
    dangChay.delete(id);
    ds = ds.map((d) => {
      if (d.id !== id) return d;
      const goc = d.original_char_count ?? 0;
      const tom = CAU.tomTatGia.length;
      return {
        ...d,
        status: 'COMPLETED',
        summary_content: CAU.tomTatGia,
        summary_char_count: tom,
        reduction_ratio: goc > 0 ? Math.max(0, Math.round((1 - tom / goc) * 1000) / 10) : 0,
        llm_model: 'mock',
        error_message: null,
        updated_at: bayGio(),
      };
    });
  }
}

function hang(d: ChiTietTomTat): TomTat {
  const { original_content: _a, summary_content: _b, original_filename: _c, token_usage: _d, error_message: _e, ...r } = d;
  return r;
}

function trangMock(): Promise<TrangTomTat> {
  capNhatTacVu();
  const hien = mockState() === 'filtered-empty' ? [] : ds;
  const tl = ds.filter((d) => d.reduction_ratio != null && d.reduction_ratio > 0).map((d) => d.reduction_ratio!);
  const rong: TrangTomTat = {
    stats: { total: 0, summarized: 0, pending: 0, avg_reduction_ratio: null },
    data: [],
    total: 0,
    page: 1,
    page_size: 100,
    total_pages: 1,
  };
  return mockRequest(
    {
      stats: {
        total: ds.length,
        summarized: ds.filter((d) => d.status === 'COMPLETED').length,
        pending: ds.filter((d) => d.status === 'PENDING').length,
        avg_reduction_ratio: tl.length ? tl.reduce((a, b) => a + b, 0) / tl.length : null,
      },
      data: [...hien].sort((a, b) => b.created_at.localeCompare(a.created_at)).map(hang),
      total: hien.length,
      page: 1,
      page_size: 100,
      total_pages: 1,
    },
    rong,
  );
}

const banGia = {
  danhSach: trangMock,
  chiTiet: (id: string): Promise<ChiTietTomTat> => {
    capNhatTacVu();
    const d = ds.find((x) => x.id === id);
    return d ? mockRequest(structuredClone(d)) : tuChoi(khongThay());
  },
  them: async (vao: ThemTomTat): Promise<TomTat> => {
    const noiDung = await vao.file.text();
    const moi: ChiTietTomTat = {
      id: idMoi(),
      document_name: vao.document_name,
      document_type: vao.document_type,
      status: 'PENDING',
      source: null,
      external_doc_id: null,
      original_char_count: [...noiDung].length,
      summary_char_count: null,
      reduction_ratio: null,
      creator_name: null,
      user_id: null,
      llm_model: null,
      created_at: bayGio(),
      updated_at: bayGio(),
      original_content: noiDung,
      summary_content: null,
      original_filename: vao.file.name,
      token_usage: null,
      error_message: null,
    };
    ds = [moi, ...ds];
    if (vao.auto_summarize) dangChay.set(moi.id, Date.now());
    return mockRequest(hang(moi));
  },
  chay: (id: string): Promise<KetQuaChayTomTat> => {
    if (!ds.some((d) => d.id === id)) return tuChoi(khongThay());
    ds = ds.map((d) => (d.id === id ? { ...d, status: 'PENDING', error_message: null, updated_at: bayGio() } : d));
    dangChay.set(id, Date.now());
    return mockRequest({ id, status: 'PENDING', message: CAU.daGuiYeuCau });
  },
  suaTomTat: (id: string, summary: string): Promise<ChiTietTomTat> => {
    const cu = ds.find((d) => d.id === id);
    if (!cu) return tuChoi(khongThay());
    const goc = cu.original_char_count ?? 0;
    const tom = [...summary].length;
    const moi: ChiTietTomTat = {
      ...cu,
      status: 'COMPLETED',
      summary_content: summary,
      summary_char_count: tom,
      reduction_ratio: goc > 0 ? Math.max(0, Math.round((1 - tom / goc) * 1000) / 10) : 0,
      updated_at: bayGio(),
    };
    ds = ds.map((d) => (d.id === id ? moi : d));
    return mockRequest(structuredClone(moi));
  },
  xoa: (id: string): Promise<void> => {
    ds = ds.filter((d) => d.id !== id);
    dangChay.delete(id);
    return mockRequest(undefined);
  },
  phanCong: (id: string): Promise<KetQuaPhanCongTuTomTat> => {
    const d = ds.find((x) => x.id === id);
    if (!d) return tuChoi(khongThay());
    if (!d.summary_content) return tuChoi({ code: 'INVALID_ARGUMENT', status: 400, message: CAU.chuaTomTat });
    return mockRequest({ summarization_id: id, created_count: 3, message: CAU.daPhanCong(3) });
  },
};

// ─── Bản thật ────────────────────────────────────────────────────────────────

const ma = (id: string) => encodeURIComponent(id);

const banThat: typeof banGia = {
  danhSach: () => goiApi<TrangTomTat>(`${GOC}/summarize`, { query: MOT_TRANG }),
  chiTiet: (id) => goiApi<ChiTietTomTat>(`${GOC}/summarize/${ma(id)}`),
  them: (vao) => {
    const form = new FormData();
    form.append('document_name', vao.document_name);
    form.append('document_type', vao.document_type);
    form.append('auto_summarize', String(vao.auto_summarize));
    form.append('file', vao.file);
    return goiApi<TomTat>(`${GOC}/summarize/file`, { method: 'POST', bieuMau: form });
  },
  chay: (id) => goiApi<KetQuaChayTomTat>(`${GOC}/summarize/${ma(id)}/run`, { method: 'POST' }),
  suaTomTat: (id, summary) =>
    goiApi<ChiTietTomTat>(`${GOC}/summarize/${ma(id)}`, { method: 'PATCH', body: { summary_content: summary } }),
  xoa: (id) => goiApi<void>(`${GOC}/summarize/${ma(id)}`, { method: 'DELETE' }),
  phanCong: (id) =>
    goiApi<KetQuaPhanCongTuTomTat>(`${GOC}/task-assignments/assign/${ma(id)}`, { method: 'POST' }),
};

const chon = () => (dungApiThat() ? banThat : banGia);

export const tomTatRepository = {
  danhSach: () => chon().danhSach(),
  chiTiet: (id: string) => chon().chiTiet(id),
  them: (vao: ThemTomTat) => chon().them(vao),
  chay: (id: string) => chon().chay(id),
  suaTomTat: (id: string, summary: string) => chon().suaTomTat(id, summary),
  xoa: (id: string) => chon().xoa(id),
  phanCong: (id: string) => chon().phanCong(id),
};
